import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { SharedModule } from '@shared/shared.module';
import { VaultDateTimePipe } from '@shared/pipes/vault-date-time.pipe';
import { StandardAccountDictionaryProposal } from '@interfaces/accounting-data-standardization/responses/standard-account-dictionary-governance.response';
import {
  DictionaryReviewGroup,
  groupProposalsForReview,
} from '@src/app/features/dashboard/accounting-data-standardization/utils/standard-account-dictionary-governance.util';
import { DictionaryProposalDocumentRequest } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-proposal-card/dictionary-proposal-card.component';
import { DictionaryReviewRowComponent } from '@src/app/features/dashboard/accounting-data-standardization/components/dictionary-review-row/dictionary-review-row.component';

/**
 * Lista "A revisar" da aba Dicionário (task 3452): as propostas do revisor que entraram sozinhas no dicionário e ainda
 * não foram revistas, agrupadas pelo documento de origem (uma versão por documento). Cada grupo tem "Manter todas" e
 * o link para o documento; cada linha, Manter e Desfazer. Só apresentação: as ações saem por `output()`.
 */
@Component({
  selector: 'app-dictionary-review-list',
  standalone: true,
  imports: [SharedModule, VaultDateTimePipe, DictionaryReviewRowComponent],
  templateUrl: './dictionary-review-list.component.html',
  styleUrl: './dictionary-review-list.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DictionaryReviewListComponent {
  readonly proposals = input.required<StandardAccountDictionaryProposal[]>();
  /** Pode manter e desfazer (permissão MANAGE). */
  readonly canManage = input(false);
  /** Alguma ação do painel em andamento: trava os botões. */
  readonly locked = input(false);
  /** Proposta sendo mantida ou desfeita agora. */
  readonly busyProposalId = input<string | null>(null);
  /** Grupo em "Manter todas" agora. */
  readonly busyGroupKey = input<string | null>(null);

  readonly keepRequested = output<StandardAccountDictionaryProposal>();
  readonly revertRequested = output<StandardAccountDictionaryProposal>();
  readonly keepAllRequested = output<DictionaryReviewGroup>();
  readonly openDocumentRequested = output<DictionaryProposalDocumentRequest>();

  protected readonly groups = computed(() => groupProposalsForReview(this.proposals()));
  private readonly expandedIds = signal<ReadonlySet<string>>(new Set());

  protected isExpanded(id: string): boolean {
    return this.expandedIds().has(id);
  }

  protected toggle(id: string): void {
    this.expandedIds.update(current => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  protected openDocument(group: DictionaryReviewGroup): void {
    if (!group.companyId || !group.extractionId) return;
    this.openDocumentRequested.emit({
      companyId: group.companyId,
      extractionId: group.extractionId,
    });
  }
}
